import { useEffect, useState } from 'react';
import { GraduationCap, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useViewMode } from '@/hooks/useViewMode';
import { usePlayerProfiles } from '@/hooks/usePlayerProfiles';

/**
 * Slim toggle row that sits just under the main header for users who have
 * BOTH a player and a coach profile. Hidden for player-only and child accounts.
 */
export function ModeSwitcher() {
  const { user } = useAuth();
  const { mode, setMode } = useViewMode();
  const { isPlayer } = usePlayerProfiles();
  const [hasCoach, setHasCoach] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user || isPlayer) { setHasCoach(false); return; }
    supabase
      .from('coach_profiles')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => { if (!cancelled) setHasCoach(!!data); });
    return () => { cancelled = true; };
  }, [user, isPlayer]);

  if (!user || isPlayer || !hasCoach) return null;

  const isPlayerMode = mode === 'player';

  return (
    <div className="w-full">
      <div className="max-w-lg mx-auto px-4 pb-2 flex items-center justify-center">
        <div
          role="tablist"
          aria-label="Switch between Player and Coach mode"
          className="relative inline-flex items-center p-1 rounded-full bg-muted/60 border border-border/60 w-[200px]"
        >
          {/* Sliding indicator */}
          <span
            aria-hidden
            className="absolute top-1 bottom-1 left-1 w-[calc(50%-4px)] rounded-full bg-primary shadow-glow-primary transition-transform duration-300 ease-out"
            style={{ transform: isPlayerMode ? 'translateX(0)' : 'translateX(100%)' }}
          />
          <button
            role="tab"
            aria-selected={isPlayerMode}
            onClick={() => setMode('player')}
            className={`relative z-10 flex-1 flex items-center justify-center gap-1.5 px-3 py-1 rounded-full text-xs font-display font-bold tracking-wide transition-colors ${
              isPlayerMode ? 'text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <User className="w-3.5 h-3.5" />
            Player
          </button>
          <button
            role="tab"
            aria-selected={!isPlayerMode}
            onClick={() => setMode('coach')}
            className={`relative z-10 flex-1 flex items-center justify-center gap-1.5 px-3 py-1 rounded-full text-xs font-display font-bold tracking-wide transition-colors ${
              !isPlayerMode ? 'text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <GraduationCap className="w-3.5 h-3.5" />
            Coach
          </button>
        </div>
      </div>
    </div>
  );
}
